import { useFrame } from '@react-three/fiber';
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { Vector3 } from 'three';

import { StaticCloud } from '0_canvas/StaticCloud/StaticCloud';
import type { GroupProps } from 'types/r3f';
import { createNoise2D } from 'utils/noise';

type CloudHandle = {
  position: Vector3;
  opacity: number;
  userData: {
    startPosition: Vector3;
    finalPosition: Vector3;
    volume: number;
  };
  isLoaded: boolean;
};

const noise = createNoise2D();

export interface CloudLayerProps extends GroupProps {
  size?: number;
  animationProgress?: number;
}

export interface CloudLayerRef {
  animationProgress: number;
}

const CloudLayer = forwardRef<CloudLayerRef, CloudLayerProps>(
  ({ size = 1000, animationProgress = 1, ...props }, ref) => {
    const cloudsRef = useRef<CloudHandle[]>([]);
    const animationProgressRef = useRef<number>(animationProgress);
    const prevAnimationProgressRef = useRef<number>(0);

    useImperativeHandle(ref, () => ({
      get animationProgress() {
        return animationProgressRef.current;
      },
      set animationProgress(n: number) {
        animationProgressRef.current = n;
      },
    }));

    const clouds = useMemo(() => {
      cloudsRef.current.length = 0;
      const acc = [];
      let count = 0;

      const density = 0.03;
      const normalBounds = new Vector3(24, 8, 24);
      const largeBounds = new Vector3(48, 8, 48);

      for (let i = 0; i < size * density; i++) {
        for (let j = 0; j < size * density; j++) {
          const xFactor = i / (size * density - 1);
          const zFactor = j / (size * density - 1);

          const dx = xFactor - 0.5;
          const dz = zFactor - 0.5;
          const totalDistance = dx * dx + dz * dz;

          if (totalDistance > 0.25) continue;

          if (totalDistance > 0.2) {
            const chance = 1 - (totalDistance - 0.2) / 0.05;
            if (Math.random() > chance) continue;
          }

          const nx = xFactor * size * 0.008;
          const nz = zFactor * size * 0.008;
          const n = Math.max(0, noise(nx, nz));

          const x = dx * size;
          const z = dz * size;

          const spawnChance = n;
          if (Math.random() > spawnChance) continue;

          let bounds = normalBounds;
          let volume = 40;
          let y = 0;

          const isLarge = Math.random() < 0.1;

          if (isLarge) {
            bounds = largeBounds;
            volume = 80;
            y = 10;
          }

          const finalPosition = new Vector3(x, y, z);

          const offset = new Vector3(
            Math.random() * 2 - 1,
            Math.random() * 2 - 1,
            Math.random() * 2 - 1
          ).multiplyScalar(volume * 0.6);

          const startPosition = finalPosition.clone().add(offset);

          const cloudIdx = count;

          const cloud = (
            <StaticCloud
              key={`${i}:${j}`}
              ref={ref => ref && (cloudsRef.current[cloudIdx] = ref as any)}
              position={startPosition.clone()}
              opacity={0}
              userData={{ startPosition, finalPosition, volume }}
              segmentCount={10}
              bounds={bounds}
              volume={volume}
              baseLight={0.5}
              fog={true}
            />
          );

          acc.push(cloud);
          count++;
        }
      }

      cloudsRef.current = Array(count).fill(null);

      return acc;
    }, [size]);

    useFrame(() => {
      if (animationProgressRef.current === prevAnimationProgressRef.current)
        return;

      const api2 = 1 - Math.pow(1 - animationProgressRef.current, 2);

      for (const cloud of cloudsRef.current) {
        if (!cloud || !cloud.isLoaded) return;

        cloud.position.lerpVectors(
          cloud.userData.startPosition,
          cloud.userData.finalPosition,
          api2
        );
        cloud.opacity = api2;
      }

      prevAnimationProgressRef.current = animationProgressRef.current;
    });

    return <group {...props}>{clouds}</group>;
  }
);

CloudLayer.displayName = 'CloudLayer';
export default CloudLayer;
